import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

function BulkUploadDialog({ open, onClose, onSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const isValidFile = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      if (!isValidFile) {
        setError('कृपया फक्त Excel फाइल निवडा (.xlsx)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    const isValidFile = file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'));
    if (isValidFile) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('कृपया फक्त Excel फाइल ड्रॉप करा');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('कृपया फाइल निवडा');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await adminAPI.bulkUploadUsers(formData);
      setUploadResults(response.data);

      if (response.data.successful > 0) {
        onSuccess();
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      setError(err.response?.data?.detail || 'अपलोड अयशस्वी झाले');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadExcelTemplate = async () => {
    try {
      const response = await adminAPI.downloadExcelTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user_bulk_upload_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel template download error:', err);
      setError('Excel टेम्पलेट डाउनलोड अयशस्वी');
    }
  };

  const handleDownloadResults = () => {
    if (!uploadResults) return;

    // Convert results to CSV
    const headers = ['Row Number', 'Full Name', 'Mobile', 'Email', 'Status', 'Message', 'User ID'];
    const csvRows = [headers.join(',')];

    uploadResults.results.forEach(result => {
      const row = [
        result.row_number,
        `"${result.full_name}"`,
        result.mobile,
        result.email || '',
        result.status,
        `"${result.message}"`,
        result.user_id || ''
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulk_upload_results_${new Date().getTime()}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResults(null);
    setError(null);
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'created':
        return 'success';
      case 'skipped_duplicate_mobile':
      case 'skipped_duplicate_email':
      case 'skipped_duplicate_asha_code':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'created':
        return 'तयार केले';
      case 'skipped_duplicate_mobile':
        return 'वगळले (मोबाइल)';
      case 'skipped_duplicate_email':
        return 'वगळले (ईमेल)';
      case 'skipped_duplicate_asha_code':
        return 'वगळले (आशा कोड)';
      case 'error':
        return 'त्रुटी';
      default:
        return status;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">बल्क वापरकर्ता अपलोड</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!uploadResults ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Excel फाइल वापरून एकाच वेळी अनेक वापरकर्ते तयार करा.
              Excel टेम्पलेटमध्ये जिल्हा आणि तालुक्यासाठी ड्रॉपडाउन आहेत.
            </Alert>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExcelTemplate}
              fullWidth
              sx={{ mb: 3 }}
            >
              Excel टेम्पलेट डाउनलोड करा
            </Button>

            <Divider sx={{ mb: 3 }} />

            <Box
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: selectedFile ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: selectedFile ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {selectedFile ? selectedFile.name : 'Excel फाइल निवडा किंवा इथे ड्रॉप करा'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                फक्त .xlsx फाइल समर्थित आहे
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  अपलोड करत आहे...
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                अपलोड पूर्ण झाले!
              </Typography>
              <Typography variant="body2">
                एकूण: {uploadResults.total_rows} | यशस्वी: {uploadResults.successful} |
                वगळले: {uploadResults.skipped} | त्रुटी: {uploadResults.errors}
              </Typography>
            </Alert>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">परिणाम</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadResults}
              >
                परिणाम डाउनलोड करा
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>पंक्ती</TableCell>
                    <TableCell>नाव</TableCell>
                    <TableCell>मोबाइल</TableCell>
                    <TableCell>स्थिती</TableCell>
                    <TableCell>संदेश</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadResults.results.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell>{result.row_number}</TableCell>
                      <TableCell>{result.full_name}</TableCell>
                      <TableCell>{result.mobile}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(result.status)}
                          color={getStatusColor(result.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {result.message}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!uploadResults ? (
          <>
            <Button onClick={handleClose}>रद्द करा</Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              अपलोड करा
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            बंद करा
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkUploadDialog;
